import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import styles from "./CartItem.module.css";
import { getPriceLabel } from "../../utils/getPriceLabel";
import {
  formatRetailPrice,
  RETAIL_PRICE_LABEL,
} from "../../utils/formatRetailPrice";
import OptionsIcon from "../../assets/Options_Icon.svg";
import CartIcon from "../../assets/Cart_Icon.svg";
import NoteIcon from "../../assets/Circle_Circle_Note_Icon.svg";
import TrashIcon from "../../assets/Trash_Icon.svg";
import ProductImage from "Shared/ProductImage";
import { resolveImageUrl } from "../../utils/resolveImageUrl";
import { CartEditModal } from "components/CartEditModal/CartEditModal";
import { AddToListModal } from "components/AddToListModal/AddToListModal";
import { lowStockMessage } from "../../utils/stockStatus";
import {
  getVariantColorKey,
  resolveVariantColorAndSize,
} from "../../utils/variantParsing";
import ItemNote from "components/ItemNote";

const abbreviateSizeLabel = (sizeLabel) => {
  const normalized = String(sizeLabel || "")
    .trim()
    .toUpperCase();

  const sizeMap = {
    SMALL: "S",
    S: "S",
    MEDIUM: "M",
    MD: "M",
    M: "M",
    LARGE: "LG",
    L: "LG",
    LG: "LG",
    "X-LARGE": "XL",
    XLARGE: "XL",
    XL: "XL",
    XXL: "X2",
    "2X": "X2",
    "3X": "X3",
  };

  return sizeMap[normalized] || sizeLabel;
};

export function CartItem({
  stockInfo,
  onDelete,
  onSave,
  variant,
  onAddToCart,
}) {
  const isSaved = variant === "saved";
  const [editOpen, setEditOpen] = useState(false);
  const [addToListOpen, setAddToListOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);

  const isConfigurable =
    String(stockInfo?.itemType || "")
      .trim()
      .toUpperCase() === "CONFIGURABLE";

  // Both /cart and list endpoints (e.g. Saved For Later) now enrich each row
  // with variants/detailColors/sizeOptions/isInList directly, so there's no
  // need for a per-row live /stock-items lookup here anymore.
  const item = useMemo(
    () => ({
      variants: stockInfo?.variants,
      detailColors: stockInfo?.detailColors,
      sizeOptions: stockInfo?.sizeOptions,
      imageUrl: stockInfo?.imageUrl ?? stockInfo?.image,
      author: stockInfo?.author,
      isInList: stockInfo?.isInList,
    }),
    [
      stockInfo?.variants,
      stockInfo?.detailColors,
      stockInfo?.sizeOptions,
      stockInfo?.imageUrl,
      stockInfo?.image,
      stockInfo?.author,
      stockInfo?.isInList,
    ]
  );

  const colorOptions = useMemo(() => {
    if (!isConfigurable) {
      return [];
    }

    if (!item?.detailColors?.length) {
      return stockInfo?.color
        ? [{ label: stockInfo.color, imageUrl: item?.imageUrl }]
        : [];
    }

    return item.detailColors.map((option) => {
      if (typeof option === "string") {
        return { label: option, imageUrl: item?.imageUrl };
      }

      return {
        label: option?.label || option?.value || "",
        imageUrl: option?.imageUrl || item?.imageUrl,
      };
    });
  }, [isConfigurable, item, stockInfo?.color]);

  const sizeOptions = useMemo(() => {
    if (!isConfigurable) {
      return [];
    }

    const options = item?.sizeOptions || [];
    if (!options.length) {
      return stockInfo?.size
        ? [{ label: stockInfo.size, isSoldOut: false }]
        : [];
    }

    return options.map((option) => {
      if (typeof option === "string")
        return { label: option, isSoldOut: false };
      return {
        label: option?.label || option?.value || "",
        isSoldOut: Boolean(option?.isSoldOut),
      };
    });
  }, [isConfigurable, item, stockInfo?.size]);

  const variantTuples = useMemo(() => {
    return (item?.variants || [])
      .filter((variant) => variant?.displayName)
      .map((variant) => {
        const { color, size } = resolveVariantColorAndSize(variant);
        // A variant is unavailable if unsellable OR (per the func-bsm-api
        // stock enforcement change) explicitly out of stock. Missing
        // isInStock isn't treated as out of stock.
        return {
          color,
          size,
          colorKey: getVariantColorKey(variant, color),
          swatchHex: variant?.swatchHex || null,
          swatchImage: variant?.swatchImage || null,
          variantId: variant?.variantId ?? null,
          variationId: variant?.variationId ?? null,
          isSellable:
            variant?.isSellable !== false && variant?.isInStock !== false,
          imageUrl: resolveImageUrl(variant) || null,
        };
      })
      .filter((tuple) => tuple.color || tuple.size);
  }, [item?.variants]);

  const variantMatrix = useMemo(() => {
    const matrix = {};
    variantTuples.forEach((tuple) => {
      const key = `${tuple.color}::${tuple.size}`;
      matrix[key] = {
        variantId: tuple.variantId,
        variationId: tuple.variationId,
        isAvailable: tuple.isSellable,
      };
    });
    return matrix;
  }, [variantTuples]);

  const finalColorOptions = useMemo(() => {
    if (variantTuples.length > 0) {
      const colorEntries = new Map();
      const colorAvailability = new Map();
      variantTuples.forEach((tuple) => {
        if (!tuple.color) return;
        if (!colorEntries.has(tuple.colorKey)) {
          colorEntries.set(tuple.colorKey, {
            label: tuple.color,
            imageUrl: tuple.imageUrl ?? null,
            swatchHex: tuple.swatchHex,
            swatchImage: tuple.swatchImage,
          });
        }
        const wasAvailable = colorAvailability.get(tuple.colorKey);
        colorAvailability.set(
          tuple.colorKey,
          typeof wasAvailable === "boolean"
            ? wasAvailable || tuple.isSellable
            : tuple.isSellable
        );
      });

      return Array.from(colorEntries.entries()).map(([key, entry]) => ({
        ...entry,
        isSoldOut: !(colorAvailability.get(key) ?? true),
      }));
    }

    return colorOptions;
  }, [variantTuples, colorOptions]);

  const finalSizeOptions = useMemo(() => {
    if (variantTuples.length > 0) {
      const sizeAvailability = new Map();
      variantTuples.forEach((tuple) => {
        if (!tuple.size) return;
        const wasAvailable = sizeAvailability.get(tuple.size);
        sizeAvailability.set(
          tuple.size,
          typeof wasAvailable === "boolean"
            ? wasAvailable || tuple.isSellable
            : tuple.isSellable
        );
      });
      return Array.from(sizeAvailability.entries()).map(
        ([label, isAvailable]) => ({
          label,
          isSoldOut: !isAvailable,
        })
      );
    }

    return sizeOptions;
  }, [variantTuples, sizeOptions]);

  const retailPriceText = formatRetailPrice(stockInfo?.retailPrice);

  const variantInfo = useMemo(() => {
    if (!stockInfo?.variantId || !item?.variants?.length) {
      return null;
    }

    const selectedVariant = item.variants.find(
      (v) => v.variantId === stockInfo.variantId
    );
    if (!selectedVariant?.displayName) {
      return null;
    }

    return resolveVariantColorAndSize(selectedVariant);
  }, [stockInfo?.variantId, item?.variants]);

  const isOverStock =
    !stockInfo.isOutOfStock &&
    typeof stockInfo.quantityAvailable === "number" &&
    (stockInfo.quantity ?? 0) > stockInfo.quantityAvailable;

  return (
    <div className={styles.container}>
      <div className={styles.row}>
        {isOverStock && (
          <span
            className={styles.overStockBadge}
            role="img"
            aria-label="Requested quantity exceeds available stock"
            title="Requested quantity exceeds available stock"
          >
            !
          </span>
        )}
        <div className={styles.leftSection}>
          <ProductImage
            className={styles.productImage}
            src={stockInfo.image}
            alt={stockInfo.title}
          />
        </div>

        <div className={styles.middleSection}>
          <div className={styles.title}>{stockInfo.title}</div>
          <div className={styles.price}>
            <span className={styles.labelCost}>
              {getPriceLabel(stockInfo?.itemType)}:{" "}
            </span>
            ${stockInfo.memberPrice.toFixed(2)}
          </div>
          {retailPriceText && (
            <div className={styles.price}>
              <span className={styles.labelCost}>{RETAIL_PRICE_LABEL}: </span>
              {retailPriceText}
            </div>
          )}

          {variantInfo && (
            <div className={styles.variantLabel}>
              <span className={styles.variantLabelKey}>Color:</span>
              <span className={styles.variantLabelValue}>
                {variantInfo.color}
              </span>
              <span className={styles.variantLabelKey}>Size:</span>
              <span className={styles.variantLabelValue}>
                {abbreviateSizeLabel(variantInfo.size)}
              </span>
            </div>
          )}

          {item?.author && (
            <div className={styles.author}>
              <span className={styles.labelAuthor}>Author: </span>
              {item.author}
            </div>
          )}

          {stockInfo.isPreOrder && (
            <div className={styles.preOrderWarning}>
              Item is pre-order only.
            </div>
          )}

          {isSaved ? (
            <div className={styles.actions}>
              {!stockInfo.isOutOfStock && (
                <button
                  type="button"
                  className={styles.iconBtn}
                  onClick={() => onAddToCart?.(stockInfo)}
                >
                  <img src={CartIcon} alt="Add to Cart" />
                </button>
              )}

              <button
                type="button"
                className={styles.iconBtn}
                onClick={() => setNoteOpen((v) => !v)}
              >
                <img src={NoteIcon} alt="Personal note" />
              </button>

              <button
                type="button"
                className={styles.iconBtn}
                onClick={() => setAddToListOpen(true)}
              >
                <img src={OptionsIcon} alt="More" />
              </button>

              <button
                type="button"
                className={styles.iconBtnDelete}
                onClick={() => onDelete?.()}
              >
                <img src={TrashIcon} alt="Delete" />
              </button>
            </div>
          ) : (
            <div className={styles.actions}>
              <button
                type="button"
                className={styles.iconBtn}
                onClick={() => setEditOpen(true)}
              >
                <img src={NoteIcon} alt="Edit" />
              </button>

              <button
                type="button"
                className={styles.iconBtn}
                onClick={() => setAddToListOpen(true)}
              >
                <img src={OptionsIcon} alt="More" />
              </button>

              <button
                type="button"
                className={styles.iconBtnDelete}
                onClick={onDelete}
              >
                <img src={TrashIcon} alt="Delete" />
              </button>
            </div>
          )}
        </div>

        {(!isSaved || stockInfo.isOutOfStock || stockInfo.isLowStock) && (
          <div className={styles.rightSection}>
            {stockInfo.isOutOfStock ? (
              <div className={styles.outOfStock}>Out of Stock</div>
            ) : (
              stockInfo.isLowStock && (
                <div className={styles.lowStock}>
                  {lowStockMessage(stockInfo.quantityAvailable)}
                </div>
              )
            )}
            {!isSaved && <>Qty: {stockInfo.quantity}</>}
          </div>
        )}
      </div>

      {isSaved && (
        <ItemNote
          isEditing={noteOpen}
          stockId={stockInfo.stockId}
          onClose={() => setNoteOpen(false)}
        />
      )}

      <CartEditModal
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        onCancel={() => setEditOpen(false)}
        onSave={(updated) => {
          onSave?.(updated);
          setEditOpen(false);
        }}
        item={{
          id: stockInfo.id,
          title: stockInfo.title,
          memberPrice: stockInfo.memberPrice,
          quantity: stockInfo.quantity,
          quantityAvailable: stockInfo.quantityAvailable,
          isConfigurable,
          color: variantInfo?.color ?? stockInfo.color,
          size: variantInfo?.size ?? stockInfo.size,
          variationId: stockInfo.variationId,
          variantId: stockInfo.variantId,
          listProductId: stockInfo.listProductId,
          image: stockInfo.image,
          isPreOrder: stockInfo.isPreOrder,
          variants: item?.variants,
          variantMatrix:
            Object.keys(variantMatrix).length > 0 ? variantMatrix : undefined,
        }}
        colorOptions={
          finalColorOptions.length > 0 ? finalColorOptions : undefined
        }
        sizeOptions={finalSizeOptions.length > 0 ? finalSizeOptions : undefined}
      />

      <AddToListModal
        isOpen={addToListOpen}
        onClose={() => setAddToListOpen(false)}
        onCancel={() => setAddToListOpen(false)}
        onAdd={() => setAddToListOpen(false)}
        item={{
          stockId: stockInfo.stockId,
          id: stockInfo.id,
          title: stockInfo.title,
          memberPrice: stockInfo.memberPrice,
          quantity: stockInfo.quantity,
          color: variantInfo?.color ?? stockInfo.color,
          size: variantInfo?.size ?? stockInfo.size,
          image: stockInfo.image,
          imageUrl: stockInfo.imageUrl ?? stockInfo.image,
          author: stockInfo.author,
          itemType: stockInfo.itemType,
          variationId: stockInfo.variationId,
          variantId: stockInfo.variantId,
          // /cart now returns isInList directly on each row, same as
          // ListItemsGet (Saved For Later etc.) and SingleStockGet.
          isInList: stockInfo.isInList,
        }}
        colorOptions={
          finalColorOptions.length > 0 ? finalColorOptions : undefined
        }
        sizeOptions={finalSizeOptions.length > 0 ? finalSizeOptions : undefined}
        variantMatrix={
          Object.keys(variantMatrix).length > 0 ? variantMatrix : undefined
        }
        showNewList={false}
        skipVariantConfirmation
      />
    </div>
  );
}

CartItem.propTypes = {
  onDelete: PropTypes.func,
  onSave: PropTypes.func,
  onAddToCart: PropTypes.func,
  variant: PropTypes.string,
  stockInfo: PropTypes.shape({
    stockId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    id: PropTypes.number,
    title: PropTypes.string.isRequired,
    memberPrice: PropTypes.number.isRequired,
    itemType: PropTypes.string,
    author: PropTypes.string,
    image: PropTypes.string.isRequired,
    quantity: PropTypes.number.isRequired,
    color: PropTypes.string,
    size: PropTypes.string,
    isPreOrder: PropTypes.bool,
    isOutOfStock: PropTypes.bool,
    isLowStock: PropTypes.bool,
    quantityAvailable: PropTypes.number,
    isInList: PropTypes.arrayOf(
      PropTypes.shape({
        listId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        title: PropTypes.string,
        code: PropTypes.string,
      })
    ),
  }).isRequired,
};

export default CartItem;
